import type { IntlShape } from 'react-intl'
import config from 'constants/config'
import { LabFeatureFlags } from 'constants/lab'
import { TakeoverId } from 'lib/lab/dynamicPayload'

export const LUX_PLUS_MONTH_TAKEOVER_ID = 'lux-plus-month'
export const LUX_PLUS_MONTH_TAKEOVER_REGIONS = new Set(['AU', 'US', 'NZ', 'CA', 'CN', 'SG', 'HK', 'ID', 'IL', 'JP', 'MO', 'KR', 'MY', 'PH', 'QA', 'SA', 'ZA', 'TW', 'TH', 'AE', 'VN', 'FR', 'DE', 'IE', 'IT', 'NL', 'RU', 'ES', 'GB'])

// TEMPORARY CAMPAIGN OVERRIDE — shared end date for all three fallbacks below (site takeover,
// offer campaign tag, header banner). Matches the real campaign banner's own endTimestamp:
// 2026-09-14 23:59 Australia/Sydney. Once this passes, all three fallbacks stop applying on their
// own (a real lab response still overrides them anyway) — delete them at your leisure afterward.
const LUX_PLUS_MONTH_FALLBACKS_END_TIMESTAMP = Date.parse('2026-09-14T13:59:00.000Z')
export function isLuxPlusMonthFallbackActive(): boolean {
  // Production-only: no real svc-lab campaign is ever configured for local/staging, so outside
  // production these fallbacks would just fabricate campaign content that was never actually
  // there before (this broke a Playwright test whose locator collided with the fallback header
  // banner's "View offers" CTA, since that banner was never present in the test environment
  // before this fallback existed).
  return config.LUXURY_PLUS_ENABLED && config.APP_ENV === 'production' && Date.now() < LUX_PLUS_MONTH_FALLBACKS_END_TIMESTAMP
}

/**
 * TEMPORARY CAMPAIGN OVERRIDE — delete this constant and its one usage in
 * src/client/reducers/system.ts (initialFeaturesState.labFlags) once the LuxPlus+ Month campaign
 * ends, to fully restore the enableSiteTakeover lab flag as the sole activation source.
 *
 * Seeds the enableSiteTakeover lab flag with this default before any real svc-lab response
 * arrives, so the takeover no longer depends on that flag staying configured/enabled in
 * www-le-lab. Any real fetched value for this flag key still overrides it (see the reducer merge
 * order), so disabling it (or pointing it at a different takeover) via lab still works as a
 * kill-switch/override — this only fills the gap when the flag is absent or was removed.
 */
export const LUX_PLUS_MONTH_ENABLE_SITE_TAKEOVER_FALLBACK_FLAG: App.LabFeatureFlagResult = {
  key: LabFeatureFlags.enableSiteTakeover,
  enabled: true,
  value: {
    type: 'siteTakeover',
    id: TakeoverId.LUX_PLUS_MONTH_TAKEOVER,
    palette: 'inverse',
    labelStyle: '#304DAB',
    labelPalette: 'default',
  },
}

export const LUX_PLUS_MONTH_CAMPAIGN_ID = 'LuxPlus+ month'

/**
 * TEMPORARY CAMPAIGN OVERRIDE — delete this constant and its one usage in
 * src/client/reducers/system.ts (initialFeaturesState.labFlags) once the LuxPlus+ Month campaign
 * ends, alongside LUX_PLUS_MONTH_ENABLE_SITE_TAKEOVER_FALLBACK_FLAG above.
 *
 * Seeds the enableOfferCampaignTag lab flag with this default so the "LuxPlus+ month" offer-card
 * badge no longer depends on that flag staying configured/enabled in www-le-lab. A real fetched
 * value for this flag key still overrides it (same merge order as the site-takeover fallback).
 */
export const LUX_PLUS_MONTH_ENABLE_OFFER_CAMPAIGN_TAG_FALLBACK_FLAG: App.LabFeatureFlagResult = {
  key: LabFeatureFlags.enableOfferCampaignTag,
  enabled: true,
  value: {
    type: 'campaignTag',
    campaignId: LUX_PLUS_MONTH_CAMPAIGN_ID,
    campaignTag: LUX_PLUS_MONTH_CAMPAIGN_ID,
  },
}
export const LUX_PLUS_MONTH_ALL_OFFERS_URL = '/lux-plus'
export const LUX_PLUS_MONTH_LANDING_PAGE_URL = '/campaign/luxplus-member-event'

/**
 * TEMPORARY CAMPAIGN OVERRIDE — delete this function and its one usage in
 * useNotificationBannerItem.tsx once the LuxPlus+ Month campaign ends, alongside the other
 * fallbacks above.
 *
 * Mirrors the real "Luxplus+ Month Site Takeover Banner" header banner (lab bannerId 34 in
 * production, 199 in staging) so the header banner no longer depends on that banner staying
 * configured/enabled in www-le-lab. Only used when no real fetched banner with this id is present
 * for the current region — a real fetched banner with the same bannerId still takes priority.
 */
export function getLuxPlusMonthFallbackHeaderBanner(bannerId: number, intl: IntlShape): App.LabHeaderBannerResult {
  const description = intl.formatMessage({
    id: 'N21Npjh5ZG0PdEWAPZIq',
    defaultMessage: '**Exclusive [luxplus] Member Event.** Bigger savings on unforgettable escapes.',
    description: 'Fallback header banner text shown for the LuxPlus+ Month campaign when the real lab-configured banner is unavailable — keep the **bold** and [luxplus] markup tokens intact, they are parsed by renderHeaderBannerRichText',
  })
  const ctaLabel = intl.formatMessage({
    id: 'o-PE9jt63FQFl4sbVh_E',
    defaultMessage: 'View offers',
    description: 'CTA button label on the fallback header banner shown for the LuxPlus+ Month campaign when the real lab-configured banner is unavailable',
  })

  return {
    id: `header-banner-${bannerId}`,
    bannerId,
    name: 'Luxplus+ Month Site Takeover Banner',
    priority: 0,
    rank: 1,
    kind: 'lux-plus',
    isCustomAppearance: false,
    textColor: '#FFFFFF',
    ctaPrimaryTextColor: '#363A45',
    description: {
      desktop: description,
    },
    background: {
      mode: 'solid',
      value: '#304DAB',
    },
    isLuxPlusBanner: true,
    cta: {
      desktop: { label: ctaLabel, path: LUX_PLUS_MONTH_LANDING_PAGE_URL, style: 'tertiary' },
      mobile: { label: ctaLabel, path: LUX_PLUS_MONTH_LANDING_PAGE_URL, style: 'tertiary' },
    },
  }
}

// These campaign assets are only uploaded to the prod image bucket (not test-images), so they must
// always be fetched from the literal production host rather than `config.IMAGE_HOST`/`getImageUrl`,
// which resolve to the per-environment host and 404 outside production.
const LUX_PLUS_MONTH_IMAGE_HOST = 'https://images.luxuryescapes.com'

// Same Cloudinary transform syntax as `getImageUrl`, pinned to `LUX_PLUS_MONTH_IMAGE_HOST`. Forced
// to WebP since these render via CSS `background: url()`, which can't content-negotiate format.
function getLuxPlusMonthImageUrl(imageId: string, width: number) {
  return `${LUX_PLUS_MONTH_IMAGE_HOST}/q_auto:eco,c_scale,w_${width}/${imageId}.webp`
}

const LUX_PLUS_MONTH_POOL_IMAGE_ID = 'luxplus-month-asset-cce5fc1a4122'

// Mobile only (see LUX_PLUS_MONTH_HERO_DESKTOP_IMAGE_URL). Full uncropped photo — this proxy
// silently ignores `c_crop` x_/y_ offsets, so the actual crop happens client-side via CSS.
export const LUX_PLUS_MONTH_HERO_IMAGE_URL = getLuxPlusMonthImageUrl(LUX_PLUS_MONTH_POOL_IMAGE_ID, 1442)

// Pre-cropped from `luxplus-month-asset-cce5fc1a4122` at x=1109,y=788,w=1145,h=674 — desktop's frame
// is a fixed 510x300 box, so this can be a real crop instead of shipping the whole photo.
const LUX_PLUS_MONTH_HERO_DESKTOP_IMAGE_ID = 'luxplus-month-asset-hero-desktop-crop-aad6c33b9cb3'
export const LUX_PLUS_MONTH_HERO_DESKTOP_IMAGE_URL = getLuxPlusMonthImageUrl(LUX_PLUS_MONTH_HERO_DESKTOP_IMAGE_ID, 1145)
// Pre-cropped square from `luxplus-month-asset-cce5fc1a4122` at x=1532,y=984,w=330,h=330. The image
// proxy cannot express crop offsets or zoom, and chained transforms return 404.
// Use a versioned id because derivative URLs remain cached for 30 days across asset re-uploads.
const LUX_PLUS_MONTH_NAV_IMAGE_ID = 'luxplus-month-asset-nav-crop-v2'
// 192px supports DPR 4 in the 48px thumbnail.
export const LUX_PLUS_MONTH_NAV_IMAGE_URL = getLuxPlusMonthImageUrl(LUX_PLUS_MONTH_NAV_IMAGE_ID, 192)

// The home page banner cycles through these three images inside a small circular blob (294x294 max
// on desktop), zoomed via background-size up to 220% — worst case ~294 * 2.2 ≈ 650px. 1x fidelity is
// enough for this small, cropped decorative accent (not a focal image), so no retina multiplier.
// Order matches how they were provided: the two new assets first, then the existing hero image last.
export const LUX_PLUS_MONTH_HOME_BANNER_IMAGE_URLS = [
  getLuxPlusMonthImageUrl(LUX_PLUS_MONTH_POOL_IMAGE_ID, 900),
  getLuxPlusMonthImageUrl('luxplus-month-asset-b0b6d015b4cc', 900),
  getLuxPlusMonthImageUrl('luxplus-month-asset-6ac1bc5ce2ca', 900),
]

export const LUX_PLUS_MONTH_BLOB_SHAPE_LARGE_RADIUS_PERCENT = '53.38%'
export const LUX_PLUS_MONTH_BLOB_SHAPE_SMALL_RADIUS_PERCENT = '3.2%'

export interface LuxPlusMonthTourVoucherTier {
  spend: number
  voucher: number
}

// Tour optional-extras voucher tiers for the LuxPlus+ Month campaign, keyed by currency code.
// Each currency's amounts are fixed campaign figures (not FX conversions of the AUD tiers) — not
// sourced from any config. Falls back to AUD for any currency not listed here.
export const LUX_PLUS_MONTH_TOUR_VOUCHER_TIERS_BY_CURRENCY: Record<string, [LuxPlusMonthTourVoucherTier, LuxPlusMonthTourVoucherTier, LuxPlusMonthTourVoucherTier]> = {
  AUD: [{ spend: 5_000, voucher: 200 }, { spend: 15_000, voucher: 500 }, { spend: 30_000, voucher: 800 }],
  NZD: [{ spend: 6_000, voucher: 240 }, { spend: 18_000, voucher: 600 }, { spend: 36_000, voucher: 960 }],
  GBP: [{ spend: 2_750, voucher: 110 }, { spend: 8_250, voucher: 275 }, { spend: 16_500, voucher: 440 }],
  USD: [{ spend: 3_750, voucher: 150 }, { spend: 11_250, voucher: 375 }, { spend: 22_500, voucher: 600 }],
  SGD: [{ spend: 4_750, voucher: 190 }, { spend: 14_250, voucher: 475 }, { spend: 28_500, voucher: 760 }],
  EUR: [{ spend: 3_250, voucher: 130 }, { spend: 9_750, voucher: 325 }, { spend: 19_500, voucher: 520 }],
  CAD: [{ spend: 5_000, voucher: 200 }, { spend: 15_000, voucher: 500 }, { spend: 30_000, voucher: 800 }],
  HKD: [{ spend: 30_000, voucher: 1_200 }, { spend: 90_000, voucher: 3_000 }, { spend: 180_000, voucher: 4_800 }],
  MYR: [{ spend: 15_000, voucher: 600 }, { spend: 45_000, voucher: 1_500 }, { spend: 90_000, voucher: 2_400 }],
  AED: [{ spend: 13_750, voucher: 550 }, { spend: 41_250, voucher: 1_375 }, { spend: 82_500, voucher: 2_200 }],
  ZAR: [{ spend: 60_000, voucher: 2_400 }, { spend: 180_000, voucher: 6_000 }, { spend: 360_000, voucher: 9_600 }],
  THB: [{ spend: 120_000, voucher: 4_800 }, { spend: 360_000, voucher: 12_000 }, { spend: 720_000, voucher: 19_200 }],
  KRW: [{ spend: 5_250_000, voucher: 210_000 }, { spend: 15_750_000, voucher: 525_000 }, { spend: 31_500_000, voucher: 840_000 }],
  IDR: [{ spend: 65_000_000, voucher: 2_600_000 }, { spend: 195_000_000, voucher: 6_500_000 }, { spend: 390_000_000, voucher: 10_400_000 }],
  PHP: [{ spend: 225_000, voucher: 9_000 }, { spend: 675_000, voucher: 22_500 }, { spend: 1_350_000, voucher: 36_000 }],
  SAR: [{ spend: 13_750, voucher: 550 }, { spend: 41_250, voucher: 1_375 }, { spend: 82_500, voucher: 2_200 }],
  JPY: [{ spend: 600_000, voucher: 24_000 }, { spend: 1_800_000, voucher: 60_000 }, { spend: 3_600_000, voucher: 96_000 }],
  CNY: [{ spend: 25_000, voucher: 1_000 }, { spend: 75_000, voucher: 2_500 }, { spend: 150_000, voucher: 4_000 }],
  QAR: [{ spend: 13_750, voucher: 550 }, { spend: 41_250, voucher: 1_375 }, { spend: 82_500, voucher: 2_200 }],
  VND: [{ spend: 100_000_000, voucher: 4_000_000 }, { spend: 300_000_000, voucher: 10_000_000 }, { spend: 600_000_000, voucher: 16_000_000 }],
  TWD: [{ spend: 120_000, voucher: 4_800 }, { spend: 360_000, voucher: 12_000 }, { spend: 720_000, voucher: 19_200 }],
  ILS: [{ spend: 11_000, voucher: 440 }, { spend: 33_000, voucher: 1_100 }, { spend: 66_000, voucher: 1_760 }],
  MOP: [{ spend: 30_000, voucher: 1_200 }, { spend: 90_000, voucher: 3_000 }, { spend: 180_000, voucher: 4_800 }],
}
